/**
 * TradingSetupConfigModal Component
 *
 * Workflow-inline modal for editing trading setup default preferences.
 * Opened via gear icon in the Trading Setup card header during the daily
 * pre-trading ritual. Edits global TradingSetupSettings defaults only —
 * session-level resolution is handled by the parent (Phase 3).
 *
 * Key behaviors:
 * - Manual Save/Discard pattern (parent-owned persistence)
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - Save-in-progress blocking: modal cannot be dismissed while saving
 * - Three configuration sections: strategy-playbook pairs, default account, focus instruments
 * - No navigation helpers (workflow-inline context, not settings dashboard)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  Alert,
  Card,
  CardHeader,
  CardBody,
  useToastNotification,
} from '@enfomentis/ui';
import { strategyService } from '@/services/StrategyService';
import { playbookService } from '@/services/PlaybookService';
import { accountService } from '@/services/AccountService';
import { instrumentService } from '@/services/InstrumentService';
import { InstrumentDropdown } from '@/components/shared/InstrumentExplorer';
import { isEntityActive } from '@/types/shared/EntityStatus';
import type {
  StrategyPlaybookPair,
  TradingSetupDefaultsUpdate,
} from '@/types/shared/TradingSetupTypes';
import type { Strategy } from '@/types/schema/Strategy';
import type { Playbook } from '@/types/schema/Playbook';
import type { Account } from '@/types/schema/Account';
import type { Instrument } from '@/types/schema/Instrument';

// ============================================================================
// Constants
// ============================================================================

const ACCOUNT_NONE_KEY = '__none__';

// ============================================================================
// Types
// ============================================================================

export interface TradingSetupConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when settings are saved - parent owns persistence */
  onSave: (output: TradingSetupDefaultsUpdate) => Promise<void>;
  /** Initial strategy-playbook pairs from current settings */
  initialPairs: StrategyPlaybookPair[];
  /** Initial default account ID from current settings */
  initialAccountId: string | undefined;
  /** Initial focus instrument IDs from current settings */
  initialFocusInstrumentIds: string[];
}

/**
 * Strategy with its active playbooks, ready for rendering.
 * Strategies with zero active playbooks are filtered out before this structure is built.
 */
interface RenderableStrategy {
  strategy: Strategy;
  activePlaybooks: Playbook[];
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build a serializable local state object from the three fields.
 * Used for both state storage and dirty comparison.
 */
function buildLocalState(
  pairs: StrategyPlaybookPair[],
  accountId: string | undefined,
  instrumentIds: string[]
): TradingSetupDefaultsUpdate {
  const state: TradingSetupDefaultsUpdate = {
    defaultStrategyPlaybookPairs: pairs.map((p) => ({ ...p })),
    defaultFocusInstrumentIds: [...instrumentIds],
  };
  if (accountId !== undefined) {
    state.defaultAccountId = accountId;
  }
  return state;
}

// ============================================================================
// Component
// ============================================================================

export const TradingSetupConfigModal: React.FC<TradingSetupConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialPairs,
  initialAccountId,
  initialFocusInstrumentIds,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localState, setLocalState] = useState<TradingSetupDefaultsUpdate>(
    () => buildLocalState([], undefined, [])
  );
  const [isSaving, setIsSaving] = useState(false);

  // Reference data loaded on modal open
  const [renderableStrategies, setRenderableStrategies] = useState<RenderableStrategy[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Strategy card expand/collapse state (explicitly initialized on each modal open)
  const [expandedStrategies, setExpandedStrategies] = useState<Set<string>>(new Set());

  // Baseline for dirty tracking (stores JSON string)
  const baselineKeyRef = useRef<string>('');

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Portal container for InstrumentDropdown popover (fixes aria-hidden warning in Modal)
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // Compute dirty state using stable JSON comparison
  const localStateKey = useMemo(() => JSON.stringify(localState), [localState]);
  const isDirty = localStateKey !== baselineKeyRef.current;

  // ============================================================================
  // Data Loading
  // ============================================================================

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened — load reference data and initialize local state
      const loadData = async () => {
        setIsLoading(true);
        try {
          const [activeStrategies, activeAccounts, allInstruments] = await Promise.all([
            strategyService.getActiveStrategies(),
            accountService.getActiveAccounts(),
            instrumentService.getAll(),
          ]);

          // Load active playbooks per strategy, filter out strategies with none
          const renderable: RenderableStrategy[] = [];
          for (const strategy of activeStrategies) {
            const allPlaybooks = await playbookService.getByStrategyId(strategy.id);
            const active = allPlaybooks.filter((p) => isEntityActive(p.status));
            if (active.length > 0) {
              renderable.push({ strategy, activePlaybooks: active });
            }
          }

          setRenderableStrategies(renderable);
          setAccounts(activeAccounts);
          setInstruments(allInstruments.filter((i) => i.status !== 'archived'));

          // Initialize expand state: auto-expand if single strategy, empty otherwise
          const singleId = renderable.length === 1 ? renderable[0]?.strategy.id : undefined;
          setExpandedStrategies(singleId ? new Set([singleId]) : new Set());

          // Initialize local editing state from props
          const initial = buildLocalState(initialPairs, initialAccountId, initialFocusInstrumentIds);
          setLocalState(initial);
          baselineKeyRef.current = JSON.stringify(initial);
          setIsSaving(false);
        } catch (err) {
          console.error('Failed to load trading setup reference data:', err);
          if (isOpen) {
            showToast({
              title: 'Load failed',
              description: 'Could not load settings data. Please try again.',
              color: 'danger',
            });
            onClose();
          }
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialPairs, initialAccountId, initialFocusInstrumentIds, showToast, onClose]);

  // ============================================================================
  // Strategy-Playbook Handlers
  // ============================================================================

  const handleToggleStrategy = useCallback(
    (strategyId: string, activePlaybooks: Playbook[]) => {
      setLocalState((prev) => {
        const currentPairs = prev.defaultStrategyPlaybookPairs;
        const allSelected = activePlaybooks.every((pb) =>
          currentPairs.some((p) => p.strategyId === strategyId && p.playbookId === pb.id)
        );

        // Remove all pairs for this strategy first
        const withoutStrategy = currentPairs.filter((p) => p.strategyId !== strategyId);

        if (allSelected) {
          // Deselect all — just remove
          return { ...prev, defaultStrategyPlaybookPairs: withoutStrategy };
        }
        // Select all active playbooks
        const newPairs = activePlaybooks.map((pb) => ({
          strategyId,
          playbookId: pb.id,
        }));
        return { ...prev, defaultStrategyPlaybookPairs: [...withoutStrategy, ...newPairs] };
      });
    },
    []
  );

  const handleTogglePlaybook = useCallback((strategyId: string, playbookId: string) => {
    setLocalState((prev) => {
      const currentPairs = prev.defaultStrategyPlaybookPairs;
      const exists = currentPairs.some(
        (p) => p.strategyId === strategyId && p.playbookId === playbookId
      );

      if (exists) {
        return {
          ...prev,
          defaultStrategyPlaybookPairs: currentPairs.filter(
            (p) => !(p.strategyId === strategyId && p.playbookId === playbookId)
          ),
        };
      }
      return {
        ...prev,
        defaultStrategyPlaybookPairs: [...currentPairs, { strategyId, playbookId }],
      };
    });
  }, []);

  // ============================================================================
  // Account Handler
  // ============================================================================

  const handleAccountChange = useCallback((keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    const selected = Array.from(keys)[0] as string | undefined;

    setLocalState((prev) => {
      if (!selected || selected === ACCOUNT_NONE_KEY) {
        // Clear account — omit defaultAccountId for exactOptionalPropertyTypes safety
        const { defaultAccountId: _, ...rest } = prev;
        return rest as TradingSetupDefaultsUpdate;
      }
      return { ...prev, defaultAccountId: selected };
    });
  }, []);

  // ============================================================================
  // Instrument Handlers
  // ============================================================================

  const handleAddInstrument = useCallback((instrumentId: string) => {
    setLocalState((prev) => {
      if (prev.defaultFocusInstrumentIds.includes(instrumentId)) return prev;
      return {
        ...prev,
        defaultFocusInstrumentIds: [...prev.defaultFocusInstrumentIds, instrumentId],
      };
    });
  }, []);

  const handleRemoveInstrument = useCallback((instrumentId: string) => {
    setLocalState((prev) => ({
      ...prev,
      defaultFocusInstrumentIds: prev.defaultFocusInstrumentIds.filter((id) => id !== instrumentId),
    }));
  }, []);

  const refreshInstruments = useCallback(async () => {
    try {
      const allInstruments = await instrumentService.getAll();
      setInstruments(allInstruments.filter((i) => i.status !== 'archived'));
    } catch (err) {
      console.error('Failed to refresh instruments:', err);
    }
  }, []);

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      await onSave(localState);

      // On successful save: update baseline (clears dirty state), then close
      baselineKeyRef.current = JSON.stringify(localState);
      onClose();
    } catch (err) {
      // On save failure: keep modal open, show error toast, do NOT reset baseline
      console.error('Trading setup defaults save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localState, onSave, onClose, showToast]);

  const handleDiscard = useCallback(() => {
    // Reset localState to baseline (clears dirty), then close modal
    const baseline = JSON.parse(baselineKeyRef.current) as TradingSetupDefaultsUpdate;
    setLocalState(baseline);
    onClose();
  }, [onClose]);

  // ============================================================================
  // Derived State for Rendering
  // ============================================================================

  const instrumentMap = useMemo(() => {
    const map = new Map<string, Instrument>();
    for (const inst of instruments) {
      map.set(inst.id, inst);
    }
    return map;
  }, [instruments]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      size="2xl"
      isDismissable={!isDirty && !isSaving}
      hideCloseButton={isDirty || isSaving}
      isKeyboardDismissDisabled={isDirty || isSaving}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Trading Setup Defaults</span>
        </ModalHeader>
        <ModalBody className="gap-6">
          <div ref={portalContainerRef}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-text-secondary">Loading...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* ============================================================ */}
              {/* Section 1: Strategy & Playbook Defaults                      */}
              {/* ============================================================ */}
              <Card shadow="none" radius="sm" className="border border-default-200">
                <CardHeader className="flex flex-col items-start px-3 py-2">
                  <span className="text-sm font-medium">Strategy & Playbook Defaults</span>
                  <span className="text-sm text-text-secondary">
                    Select which strategy-playbook pairs to pre-select in new sessions
                  </span>
                </CardHeader>
                <CardBody className="border-t border-default-200 px-3 py-3">
                  {renderableStrategies.length === 0 ? (
                    <Alert
                      color="warning"
                      title="No strategies with active playbooks"
                      description="Configure strategies and playbooks in Settings to use this section."
                    />
                  ) : (
                    <div className="space-y-2">
                      {renderableStrategies.map(({ strategy, activePlaybooks }) => {
                        const selectedCount = activePlaybooks.filter((pb) =>
                          localState.defaultStrategyPlaybookPairs.some(
                            (p) => p.strategyId === strategy.id && p.playbookId === pb.id
                          )
                        ).length;
                        const allSelected = selectedCount === activePlaybooks.length;
                        const isIndeterminate = selectedCount > 0 && !allSelected;
                        const isExpanded = expandedStrategies.has(strategy.id);

                        return (
                          <Card
                            key={strategy.id}
                            shadow="none"
                            radius="sm"
                            className="border border-default-200"
                          >
                            <CardHeader className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  isSelected={allSelected}
                                  isIndeterminate={isIndeterminate}
                                  onValueChange={() =>
                                    handleToggleStrategy(strategy.id, activePlaybooks)
                                  }
                                >
                                  <span className="text-sm font-medium">{strategy.name}</span>
                                </Checkbox>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-default-400">
                                  {selectedCount}/{activePlaybooks.length}
                                </span>
                                <Button
                                  size="sm"
                                  variant="light"
                                  onPress={() =>
                                    setExpandedStrategies((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(strategy.id)) {
                                        next.delete(strategy.id);
                                      } else {
                                        next.add(strategy.id);
                                      }
                                      return next;
                                    })
                                  }
                                >
                                  {isExpanded ? 'Hide' : 'Show'}
                                </Button>
                              </div>
                            </CardHeader>
                            {isExpanded && (
                              <CardBody className="border-t border-default-200 p-3 space-y-1">
                                {activePlaybooks.map((pb) => {
                                  const isSelected =
                                    localState.defaultStrategyPlaybookPairs.some(
                                      (p) =>
                                        p.strategyId === strategy.id && p.playbookId === pb.id
                                    );
                                  return (
                                    <Checkbox
                                      key={pb.id}
                                      isSelected={isSelected}
                                      onValueChange={() =>
                                        handleTogglePlaybook(strategy.id, pb.id)
                                      }
                                    >
                                      <span className="text-sm">{pb.name}</span>
                                    </Checkbox>
                                  );
                                })}
                              </CardBody>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* ============================================================ */}
              {/* Section 2: Default Account                                   */}
              {/* ============================================================ */}
              <Card shadow="none" radius="sm" className="border border-default-200">
                <CardHeader className="flex flex-col items-start px-3 py-2">
                  <span className="text-sm font-medium">Default Account</span>
                  <span className="text-sm text-text-secondary">
                    Pre-select a trading account for new sessions
                  </span>
                </CardHeader>
                <CardBody className="border-t border-default-200 px-3 py-3">
                  {accounts.length === 0 ? (
                    <Alert
                      color="warning"
                      title="No accounts configured"
                      description="Add trading accounts in Settings to use this section."
                    />
                  ) : (() => {
                    const isSingleAccount = accounts.length === 1;
                    const savedIsActive = localState.defaultAccountId
                      ? accounts.some((a) => a.id === localState.defaultAccountId)
                      : false;
                    const displayAccountId = savedIsActive
                      ? localState.defaultAccountId
                      : isSingleAccount
                        ? accounts[0]?.id
                        : undefined;

                    return (
                      <>
                        <Select
                          label="Account"
                          placeholder="No default account"
                          selectedKeys={
                            new Set(displayAccountId ? [displayAccountId] : [])
                          }
                          onSelectionChange={handleAccountChange}
                          isDisabled={isSingleAccount}
                          disableAnimation
                          {...(isSingleAccount && {
                            classNames: {
                              base: '!opacity-100 !cursor-default',
                              value: '!text-text-primary',
                              selectorIcon: '!opacity-0',
                            },
                          })}
                        >
                          {[
                            ...(!isSingleAccount
                              ? [<SelectItem key={ACCOUNT_NONE_KEY}>None</SelectItem>]
                              : []),
                            ...accounts.map((account) => (
                              <SelectItem key={account.id}>{account.name}</SelectItem>
                            )),
                          ]}
                        </Select>
                        {isSingleAccount && (
                          <span className="text-xs text-text-secondary mt-1">
                            Only one active account available
                          </span>
                        )}
                      </>
                    );
                  })()}
                </CardBody>
              </Card>

              {/* ============================================================ */}
              {/* Section 3: Default Focus Instruments                         */}
              {/* ============================================================ */}
              <Card shadow="none" radius="sm" className="border border-default-200">
                <CardHeader className="flex flex-col items-start px-3 py-2">
                  <span className="text-sm font-medium">Default Focus Instruments</span>
                  <span className="text-sm text-text-secondary">
                    Pre-select instruments to focus on in new sessions
                  </span>
                </CardHeader>
                <CardBody className="border-t border-default-200 px-3 py-3">
                  {instruments.length === 0 ? (
                    <Alert
                      color="warning"
                      title="No instruments configured"
                      description="Add instruments in Settings to use this section."
                    />
                  ) : (
                    <>
                      {/* Add instrument picker */}
                      <InstrumentDropdown
                        value={null}
                        onChange={handleAddInstrument}
                        label="Add instrument"
                        placeholder="Select instrument to add"
                        size="sm"
                        disableAnimation
                        excludeIds={localState.defaultFocusInstrumentIds}
                        {...(portalContainerRef.current && {
                          popoverProps: { portalContainer: portalContainerRef.current },
                        })}
                        onInstrumentsChanged={refreshInstruments}
                      />

                      {/* Selected instruments as removable chips */}
                      {localState.defaultFocusInstrumentIds.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {localState.defaultFocusInstrumentIds.map((id) => {
                            const inst = instrumentMap.get(id);
                            return (
                              <Chip
                                key={id}
                                variant="flat"
                                onClose={() => handleRemoveInstrument(id)}
                              >
                                {inst ? inst.symbol : id}
                              </Chip>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </div>
          )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isDirty || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
